import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, MessageSquare, Crown, Loader2 } from "lucide-react";
import { supabase } from "@core/lib/supabase";

interface FormMetrics {
  forms_total: number;
  forms_active: number;
  forms_new_30d: number;
  responses_total: number;
  responses_30d: number;
  pro_subscribers: number;
  mrr_eur: number;
}

/**
 * Dashboard admin SCOPÉ au produit form (≠ Pulse global qui agrège tous les
 * produits au niveau compte). Source : RPC `get_form_admin_metrics` (admin-only,
 * filtre `polls.type='form'` + `plan_type='dooquest_pro'`).
 */
export function FormMetricsDashboard() {
  const { t } = useTranslation("forms");
  const [metrics, setMetrics] = useState<FormMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase.rpc("get_form_admin_metrics");
      if (!active) return;
      if (error) {
        setError(error.message);
      } else {
        setMetrics(data as unknown as FormMetrics);
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-1">
          {t("adminMetrics.title")}
        </h1>
        <p className="text-sm text-gray-400">{t("adminMetrics.subtitle")}</p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-400 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" />
          {t("adminMetrics.loading")}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
          {t("adminMetrics.error")} {error}
        </div>
      )}

      {metrics && !loading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <MetricCard
            icon={<FileText className="w-5 h-5 text-violet-400" />}
            label={t("adminMetrics.forms")}
            value={metrics.forms_total}
            sub={`${metrics.forms_active} ${t("adminMetrics.formsActive")} · +${metrics.forms_new_30d} ${t("adminMetrics.new30d")}`}
          />
          <MetricCard
            icon={<MessageSquare className="w-5 h-5 text-blue-400" />}
            label={t("adminMetrics.responses")}
            value={metrics.responses_total}
            sub={`+${metrics.responses_30d} ${t("adminMetrics.new30d")}`}
          />
          <MetricCard
            icon={<Crown className="w-5 h-5 text-amber-400" />}
            label={t("adminMetrics.proSubscribers")}
            value={metrics.pro_subscribers}
            sub={`${metrics.mrr_eur} € ${t("adminMetrics.mrr")}`}
          />
        </div>
      )}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub: string;
}) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-400 mb-3">
        {icon}
        {label}
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
      <div className="text-xs text-gray-500 mt-2">{sub}</div>
    </div>
  );
}

export default FormMetricsDashboard;
